import {
  LinearProgress,
  Box,
  Button,
  Typography,
  Chip,
  ListItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRateLimit } from "hooks/useRateLimit";
import { useAuth } from "hooks/useAuth";
import { SettingsRow, SettingsSection } from "../SettingsSection";
import { getTrialLengthForRole } from "constants/trials";

const UsageDisplay = ({ userData = {}, subscription }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("account");
  const { hasActiveTrial, hasActiveDailyPass, subscriptionTier, organization } =
    useAuth();
  const rateLimit = useRateLimit(userData, subscription, organization);

  // Check if we're in a debug environment (staging or sandbox)
  const isDebugEnv = ["staging", "sandbox"].includes(
    process.env.REACT_APP_ENVIRONMENT,
  );

  // A tier id ("small", "medium", "large") as shown, capitalized when it has no translation.
  const tierName = (tier) =>
    t(`tiers.${String(tier).toLowerCase()}`, {
      defaultValue: tier.charAt(0).toUpperCase() + tier.slice(1),
    });

  // Determine user status for display
  const getUserStatus = () => {
    if (hasActiveDailyPass) {
      const tier = userData?.subscriptionTier || "Medium";
      return t("usage.dailyPass", { tier: tierName(tier) });
    }
    if (hasActiveTrial) {
      return t("usage.trial", { count: getTrialLengthForRole(userData?.role) });
    }
    if (subscription) {
      const tier = subscriptionTier;

      if (tier) {
        return t("usage.plan", { tier: tierName(tier) });
      }
      return t("usage.subscribed"); // fallback for users without tier data
    }
    return t("usage.free");
  };

  const getUserStatusColor = () => {
    if (hasActiveTrial || hasActiveDailyPass || subscription) return "primary";
    return "default";
  };

  // Format token limit for display
  const formatTokenLimit = (limit) => {
    if (limit >= 1000000) {
      return `${(limit / 1000000).toFixed(1)}M`;
    }
    if (limit >= 1000) {
      return `${limit / 1000}K`;
    }
    return limit.toString();
  };

  const tokenLabel = isDebugEnv
    ? t("usage.tokensDebug", {
        used: rateLimit.tokensUsed?.toLocaleString(),
        limit: rateLimit.tokenLimit?.toLocaleString(),
      })
    : rateLimit.isOrgProfessional
      ? t("usage.orgPool", { limit: formatTokenLimit(rateLimit.tokenLimit) })
      : hasActiveTrial
        ? t("usage.trialCapacity", {
            limit: formatTokenLimit(rateLimit.tokenLimit),
          })
        : !rateLimit.isFreeTier
          ? t("usage.monthlyCapacityLimit", {
              limit: formatTokenLimit(rateLimit.tokenLimit),
            })
          : t("usage.monthlyCapacity");

  const upgradeLabel = rateLimit.isFreeTier
    ? rateLimit.tokenPercentage >= 100
      ? t("usage.upgradeMonthly")
      : t("usage.upgradeDaily")
    : rateLimit.tokenPercentage >= 80 &&
        subscriptionTier !== "large" &&
        !hasActiveTrial
      ? t("usage.upgradeTo", {
          tier: t(
            subscriptionTier === "small" ? "tiers.medium" : "tiers.large",
          ),
        })
      : null;

  return (
    <SettingsSection
      title={t("usage.title")}
      aside={
        <Chip
          size="small"
          label={getUserStatus()}
          color={getUserStatusColor()}
          variant="outlined"
        />
      }
    >
      <UsageMeter
        label={tokenLabel}
        percentage={rateLimit.tokenPercentage}
        color={rateLimit.getProgressColor(rateLimit.tokenPercentage)}
        note={
          rateLimit.nextReset &&
          rateLimit.tokenPercentage > rateLimit.messagePercentage
            ? t(
                rateLimit.isOrgProfessional
                  ? "usage.resetsShared"
                  : "usage.resets",
                {
                  when: rateLimit.nextReset,
                },
              )
            : null
        }
      />

      {/* Daily messages only limit the free tier. */}
      {rateLimit.isFreeTier && rateLimit.tokenPercentage < 100 && (
        <UsageMeter
          label={t("usage.dailyMessages", {
            used: rateLimit.messageCount,
            limit: rateLimit.messageLimit,
          })}
          percentage={rateLimit.messagePercentage}
          color={rateLimit.getProgressColor(rateLimit.messagePercentage)}
          note={
            rateLimit.nextReset
              ? t("usage.resets", { when: rateLimit.nextReset })
              : null
          }
        />
      )}

      {upgradeLabel && (
        <SettingsRow
          label={t("usage.needMore")}
          detail={upgradeLabel}
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/dashboard/upgrade")}
            >
              {t("usage.upgrade")}
            </Button>
          }
        />
      )}
    </SettingsSection>
  );
};

const UsageMeter = ({ label, percentage, color, note }) => {
  const { t } = useTranslation("account");
  return (
    <ListItem divider disableGutters sx={{ display: "block", py: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body1">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("usage.percentUsed", { percent: Math.floor(percentage) })}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={color}
        sx={{ height: 8, borderRadius: 4, bgcolor: "action.hover" }}
      />
      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          {note}
        </Typography>
      )}
    </ListItem>
  );
};

export default UsageDisplay;
